import PropTypes from "prop-types";
import SavedItemsProductTileList from "../../../containers/common/SavedItemsProductTileList";
import LazyLoadObserver from "../../../containers/common/LazyLoadObserver";
import { isFullyLoaded } from "../../../../state/modules/savedItems/selectors/itemStateSelectors";
import isLastBestPriceEnabled from "../../common/SavedItemsProductTiles/isLastBestPriceEnabled";
import AppDownloadBanner from "../../common/AppDownloadBanner";
import NotificationsOptInTile from "../../../containers/common/NotificationsOptInTile";
import useHandleResize from "./useHandleResize";
import useMatchMedia from "../../common/useMatchMedia";
import webContextProvider from "../../../../providers/webContextProvider";
import { isTablet } from "../ProductTiles/utils";
import ProductTilePlaceholder from "../ProductTilePlaceholder";
import media from "../../../../styles/customMedia.json";
import GoogleAdvert from "../GoogleAdvert";
import styles from "../../common/ProductTilesWrapper/index.css";
import useProductRowsInView from "./useProductRowsInView";

const SavedProductTile = ({
  item,
  index,
  ProductTile,
  showDiscountLabel,
  showExtendedPricing,
  isAuthenticated,
  rowVisibilityRef
}) =>
  isFullyLoaded(item) ? (
    <ProductTile
      {...{
        ...item,
        index,
        showDiscountLabel,
        showExtendedPricing,
        isAuthenticated,
        rowVisibilityRef
      }}
    />
  ) : (
    <ProductTilePlaceholder itemState={item.itemState} />
  );

SavedProductTile.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  ProductTile: PropTypes.elementType.isRequired,
  showDiscountLabel: PropTypes.bool.isRequired,
  showExtendedPricing: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  rowVisibilityRef: PropTypes.func
};

const getShouldShowQrCode = ({
  isLargeViewport,
  platform,
  isTab,
  savedItemsCount
}) =>
  isLargeViewport && platform === "desktop" && !isTab && savedItemsCount > 0;

const SavedItemsProductTiles = ({
  savedItems,
  ProductTile,
  showDiscountLabel,
  isAuthenticated,
  showNotificationsOptInTile
}) => {
  const {
    customMedia: {
      ["--medium-viewport"]: mediumViewport,
      ["--large-viewport"]: largeViewport
    }
  } = media;

  const showExtendedPricing = isLastBestPriceEnabled();
  const { adPosition } = useHandleResize();
  const isMediumViewport = useMatchMedia(mediumViewport);
  const isLargeViewport = useMatchMedia(largeViewport);
  const { platform } = webContextProvider();
  const isTab = isTablet();

  const { getRefForFirstItemInRow } = useProductRowsInView({
    isLargeViewport,
    isMediumViewport,
    items: savedItems,
    leadingGridItemCount: showNotificationsOptInTile ? 1 : 0
  });

  const savedProductTilesCollection = savedItems.map((item, index) => {
    const rowVisibilityRef = getRefForFirstItemInRow(index);

    return (
      <li key={item.id}>
        <SavedProductTile
          {...{
            item,
            index,
            ProductTile,
            showDiscountLabel,
            showExtendedPricing,
            isAuthenticated,
            rowVisibilityRef
          }}
        />
      </li>
    );
  });

  if (showNotificationsOptInTile) {
    savedProductTilesCollection.unshift(
      <li key="stock-alerts-opt-in-tile">
        <NotificationsOptInTile />
      </li>
    );
  }

  const shouldShowQrCode = getShouldShowQrCode({
    isLargeViewport,
    platform,
    isTab,
    savedItemsCount: savedItems.length
  });

  if (shouldShowQrCode) {
    savedProductTilesCollection.push(
      <li key="app-download-banner">
        <AppDownloadBanner />
      </li>
    );
  }

  savedProductTilesCollection.splice(
    adPosition,
    0,
    <li
      id="google-advert-list-item"
      key="google-advert"
      className={styles.fullWidthListItem}
    >
      <GoogleAdvert
        savedItemsCount={savedItems.length}
        adPosition={adPosition}
      />
    </li>
  );

  return (
    <>
      <SavedItemsProductTileList>
        {savedProductTilesCollection}
      </SavedItemsProductTileList>
      <LazyLoadObserver />
    </>
  );
};

SavedItemsProductTiles.propTypes = {
  ProductTile: PropTypes.elementType.isRequired,
  savedItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      variants: PropTypes.array
    }).isRequired
  ),
  showDiscountLabel: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  showNotificationsOptInTile: PropTypes.bool
};

export default SavedItemsProductTiles;
